import React from 'react';
import { StarFilled } from '@ant-design/icons';
import Modal from 'src/design-system/components/atoms/Modal/Modal';
import Tooltip from 'src/design-system/components/atoms/Tooltip/Tooltip';
import FixedHeaderTable from 'src/design-system/components/molecules/FixedHeaderTable/FixedHeaderTable';
import withStyles from 'react-jss';
import { CssType, ThemeType } from '@theming/jssTypes';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { getRetailTripDetailsRoute } from '@routing/utils';

const styles = (theme: ThemeType): CssType => ({
  modal: {
    '& .ant-modal-body': {
      padding: theme.spacing[4],
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .ant-modal-header': {
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      paddingBottom: theme.spacing[4],
    },
  },

  title: {
    margin: theme.spacing[0],
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: theme.spacing[4],
    color: theme.color.semantic.text.primary,
  },

  consignmentHeader: {
    fontSize: theme.typography.fontSize.md,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.color.semantic.text.secondary,
    marginBottom: theme.spacing[4],
    padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    borderRadius: theme.radius.md,
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
  },

  table: {
    marginLeft: theme.spacing[0],
  },

  ratingPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
    borderRadius: theme.radius.md,
    fontSize: theme.typography.fontSize.md,
    fontWeight: theme.typography.fontWeight.semibold,
    lineHeight: 1,
    border: `${theme.border.width.sm} solid transparent`,
  },
  ratingIcon: { fontSize: theme.typography.fontSize.md, lineHeight: 1 },

  smiley: { fontSize: theme.typography.fontSize.xxxl, lineHeight: 1 },

  reasonTags: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing[2] },
  reasonTag: {
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    color: theme.color.semantic.text.secondary,
    border: theme.spacing[0],
    borderRadius: theme.radius.sm,
    padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
  },

  feedbackText: {
    maxWidth: '380px',
    wordBreak: 'break-word',
    lineHeight: theme.typography.lineHeight.relaxed,
  },

  taskType: {
    color: theme.color.semantic.brand.default.secondary,
    fontFamily: 'monospace',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
  },

  tripNumber: {
    color: theme.color.semantic.brand.default.secondary,
    textDecoration: 'none',
    fontWeight: theme.typography.fontWeight.semibold,
    '&:hover': { textDecoration: 'none' },
  },

  timestamp: { fontSize: theme.typography.fontSize.sm, color: theme.color.semantic.text.tertiary },
});

// Rating color palette - using closest semantic tokens
const getPalette = (theme: ThemeType) => ({
  red: {
    text: theme.color.semantic.brand.danger.primary,
    bg: theme.color.semantic.brand.danger.inverse,
    border: theme.color.semantic.brand.danger.subtle,
  },
  orange: {
    text: theme.color.semantic.accent.orange.strong,
    bg: theme.color.semantic.accent.orange.subtle,
    border: theme.color.semantic.accent.orange.strong,
  },
  green: {
    text: theme.color.semantic.accent.green.strong,
    bg: theme.color.semantic.accent.green.subtle,
    border: theme.color.semantic.accent.green.strong,
  },
});

const starColors = (palette: ReturnType<typeof getPalette>, rating: number) => {
  if (rating <= 1) return palette.red;
  if (rating <= 3) return palette.orange;
  return palette.green;
};

const tenPointColors = (palette: ReturnType<typeof getPalette>, rating: number) => {
  if (rating <= 3) return palette.red;
  if (rating <= 8) return palette.orange;
  return palette.green;
};

interface CustomerFeedbackModalProps {
  isVisible: boolean;
  onModalClose: () => void;
  feedbackData: any[];
  consignmentNumber?: string;
  classes: any;
  theme: ThemeType;
}

const CustomerFeedbackModal: React.FC<CustomerFeedbackModalProps> = ({
  isVisible,
  onModalClose,
  feedbackData,
  consignmentNumber,
  classes,
  theme,
}) => {
  const { t } = useTranslation();
  const PALETTE = getPalette(theme);

  const renderStarRating = (rating: number) => {
    const { text, bg, border } = starColors(PALETTE, rating);
    return (
      <div
        className={classes.ratingPill}
        style={{ color: text, backgroundColor: bg, borderColor: border }}
      >
        <StarFilled className={classes.ratingIcon} />
        {rating}
      </div>
    );
  };

  const renderTenPointRating = (rating: number) => {
    const { text, bg, border } = tenPointColors(PALETTE, rating);
    return (
      <div
        className={classes.ratingPill}
        style={{ color: text, backgroundColor: bg, borderColor: border, justifyContent: 'center' }}
      >
        {rating}
      </div>
    );
  };

  const renderSmileyRating = (rating: number) => {
    const emoji = rating <= 1 ? '😞' : rating === 2 ? '😐' : '😊';
    return (
      <Tooltip title={`Rating: ${rating}`} placement="top">
        <span
          className={classes.smiley}
          role="img"
          aria-label={`Rating ${rating}`}
        >
          {emoji}
        </span>
      </Tooltip>
    );
  };

  const renderDefaultRating = (rating: number) => (
    <div
      className={classes.ratingPill}
      style={{
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        borderColor: theme.color.semantic.border.primary,
        color: theme.color.semantic.text.secondary,
        justifyContent: 'center'
      }}
    >
      {Number.isFinite(rating) ? rating : '-'}
    </div>
  );

  const renderRating = (rating: number, ratingType?: string) => {
    switch (ratingType) {
      case 'stars':
        return renderStarRating(rating);
      case 'ten_point':
        return renderTenPointRating(rating);
      case 'smiley':
        return renderSmileyRating(rating);
      default:
        return renderDefaultRating(rating);
    }
  };

  const renderReasonTags = (reasonList: Array<{ id: string; name: string }>) => {
    if (!reasonList || reasonList.length === 0) return '-';
    return (
      <div className={classes.reasonTags}>
        {reasonList.map((reason, index) => (
          <span key={index} className={classes.reasonTag}>
            {reason.name}
          </span>
        ))}
      </div>
    );
  };

  const renderTripNumber = (tripNumber: string) => {
    if (!tripNumber) return '-';
    return (
      <Link to={getRetailTripDetailsRoute(tripNumber)} className={classes.tripNumber}>
        {tripNumber}
      </Link>
    );
  };

  const columns = [
    {
      title: t('Task Type'),
      dataIndex: 'task_type',
      key: 'task_type',
      width: 140,
      render: (taskType: string) => (taskType ? <span className={classes.taskType}>{t(taskType)}</span> : '-'),
    },
    {
      title: t('Rating'),
      dataIndex: 'rating',
      key: 'rating',
      width: 120,
      render: (_: number, record: any) => renderRating(record.rating, record.rating_type),
    },
    {
      title: t('Reason'),
      dataIndex: 'reason_list',
      key: 'reason_list',
      width: 240,
      render: (reasonList: Array<{ id: string; name: string }>) => renderReasonTags(reasonList),
    },
    {
      title: t('Feedback Text'),
      dataIndex: 'feedback_text',
      key: 'feedback_text',
      width: 400,
      render: (text: string) => <div className={classes.feedbackText}>{text || '-'}</div>,
    },
    {
      title: t('Trip Number'),
      dataIndex: 'trip_reference_number',
      key: 'trip_reference_number',
      width: 140,
      render: (tripNumber: string) => renderTripNumber(tripNumber),
    },
    {
      title: t('Time of Feedback'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      render: (timestamp: string) => (
        <div className={classes.timestamp}>
          {timestamp ? moment(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </div>
      ),
    },
  ];


  return (
    <Modal
      title={null}
      visible={isVisible}
      onCancel={onModalClose}
      footer={null}
      width={900}
      className={classes.modal}
      destroyOnClose
    >
      <div>
        <h3 className={classes.title}>{t('Customer Feedback')}</h3>

        {consignmentNumber && (
          <div className={classes.consignmentHeader}>
            {t('Consignment Number')}: {consignmentNumber}
          </div>
        )}

        <FixedHeaderTable
          dataSource={feedbackData}
          columns={columns}
          pagination={false}
          className={classes.table}
          rowKey={(record, index) => record.task_id || index}
          scroll={{ x: true, y: 400 }}
          minHeight={400}
        />
      </div>
    </Modal>
  );
};

const CustomerFeedbackModalStyled = withStyles(styles, { injectTheme: true })(CustomerFeedbackModal);

export default CustomerFeedbackModalStyled;
